import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  UploadCloud, Trash2, Edit3, CheckCircle, 
  XCircle, Image as ImageIcon, Plus, X 
} from 'lucide-react';
import './AdminTrade.css';

const API_URL = 'https://api.sohanihandicrafts.com:5010';

const AdminTrade = () => {
    const [trades, setTrades] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Form States
    const [editingId, setEditingId] = useState(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [images, setImages] = useState([]); // For new uploads
    const [previews, setPreviews] = useState([]); // For UI previews
    const [existingImages, setExistingImages] = useState([]); // For editing

    const fileInputRef = useRef(null);

    const fetchTrades = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/trade/all`);
            setTrades(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchTrades(); }, []);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + existingImages.length > 10) {
            return Swal.fire('Limit Exceeded', 'Maximum 10 images allowed per event', 'warning');
        }
        setImages(files);
        const filePreviews = files.map(file => URL.createObjectURL(file));
        setPreviews(filePreviews);
    };

    const resetForm = () => {
        setEditingId(null);
        setTitle('');
        setDescription('');
        setImages([]);
        setPreviews([]);
        setExistingImages([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        
        images.forEach(img => {
            formData.append('tradeImages', img);
        });

        const url = editingId ? `${API_URL}/api/trade/update/${editingId}` : `${API_URL}/api/trade/add`;
        const method = editingId ? 'put' : 'post';

        try {
            await axios({ method, url, data: formData });
            Swal.fire('Success', editingId ? 'Event Updated' : 'Event Published', 'success');
            resetForm();
            fetchTrades();
        } catch (err) {
            Swal.fire('Error', 'Operation failed', 'error');
        }
    };

    const handleEdit = (event) => {
        setEditingId(event._id);
        setTitle(event.title);
        setDescription(event.description);
        setExistingImages(event.images);
        setPreviews([]); // Clear any unsaved file previews
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Delete this exhibition?',
            text: "This will remove all associated images permanently.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it'
        }).then(async (result) => {
            if (result.isConfirmed) {
                await axios.delete(`${API_URL}/api/trade/delete/${id}`);
                fetchTrades();
                Swal.fire('Deleted', 'Exhibition removed.', 'success');
            }
        });
    };

    const handleToggle = async (id) => {
        await axios.patch(`${API_URL}/api/trade/toggle/${id}`);
        fetchTrades();
    };

    return (
        <div className="adm-trd-wrapper">
            <div className="adm-trd-header">
                <h1>Exhibition Manager</h1>
                <p>Showcase your artisan journey and trade fair presence</p>
            </div>

            <div className="adm-trd-layout">
                
                {/* 1. FORM CARD */}
                <div className="adm-trd-card form-card">
                    <div className="adm-trd-card-head">
                        <h3>{editingId ? 'Edit Exhibition' : 'Add New Event'}</h3>
                        {editingId && <button className="reset-btn" onClick={resetForm}><X size={16}/></button>}
                    </div>
                    <form onSubmit={handleSubmit} className="adm-trd-form">
                        <div className="adm-trd-group">
                            <label>Event Title</label>
                            <input type="text" placeholder="e.g. IHGF Delhi Fair 2024" value={title} onChange={(e) => setTitle(e.target.value)} required />
                        </div>

                        <div className="adm-trd-group">
                            <label>Exhibition Description</label>
                            <textarea placeholder="Describe your stall and experience..." value={description} onChange={(e) => setDescription(e.target.value)} required />
                        </div>

                        <div className="adm-trd-group">
                            <label>Event Gallery (Max 10)</label>
                            <div className="adm-trd-upload-box" onClick={() => fileInputRef.current.click()}>
                                <input type="file" multiple hidden ref={fileInputRef} onChange={handleFileChange} accept="image/*" />
                                <UploadCloud size={30} />
                                <span>{editingId ? 'Add more photos' : 'Click to upload photos'}</span>
                            </div>
                            
                            {/* Previews of new files being uploaded */}
                            {(previews.length > 0 || existingImages.length > 0) && (
                                <div className="adm-trd-preview-grid">
                                    {existingImages.map((img, i) => (
                                        <div key={i} className="preview-item existing">
                                            <img src={`${API_URL}/${img}`} alt="old" />
                                            <span className="badge">Saved</span>
                                        </div>
                                    ))}
                                    {previews.map((url, i) => (
                                        <div key={i} className="preview-item new">
                                            <img src={url} alt="new" />
                                            <span className="badge">New</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <button type="submit" className="adm-trd-submit">
                            {editingId ? 'Update Exhibition' : 'Publish Event'}
                        </button>
                    </form>
                </div>

                {/* 2. TABLE CARD */}
                <div className="adm-trd-card table-card">
                    <div className="adm-trd-card-head">
                        <h3>Event Inventory</h3>
                    </div>
                    <div className="adm-trd-table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>Media</th>
                                    <th>Title & Description</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trades.map((t) => (
                                    <tr key={t._id}>
                                        <td width="120">
                                            <div className="adm-trd-stack">
                                                <img src={`${API_URL}/${t.images[0]}`} alt="thumb" />
                                                <span className="stack-count">{t.images.length}P</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="adm-trd-info">
                                                <strong>{t.title}</strong>
                                                <p className="trd-truncate">{t.description}</p>
                                            </div>
                                        </td>
                                        <td width="100">
                                            <button 
                                                className={`status-pill ${t.status ? 'active' : 'off'}`}
                                                onClick={() => handleToggle(t._id)}
                                            >
                                                {t.status ? 'Live' : 'Hidden'}
                                            </button>
                                        </td>
                                        <td width="120">
                                            <div className="adm-trd-btns">
                                                <button className="edit" onClick={() => handleEdit(t)}><Edit3 size={16}/></button>
                                                <button className="del" onClick={() => handleDelete(t._id)}><Trash2 size={16}/></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {trades.length === 0 && <div className="empty">No events found.</div>}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminTrade;